import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import { EventBriefDto } from '../../../services/api/sharedTypes/Event'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import TableButtonDelete from '../../../components/table/TableButtonDelete'
import TableButtonOpen from '../../../components/table/TableButtonOpen'
import { RouteConstants } from '../../../navigation/navigation-types'
import PaymentStateTag from '../../../components/common/PaymentStateTag'
import OrderStateTag from '../../../components/common/OrderStateTag'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { getEventTableRowColor } from '../../../utils/event-utils'
import { getNumberRoundedInFormattedString } from '../../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'startsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Klient',
    },
    {
        id: 'address',
        numeric: false,
        disablePadding: false,
        label: 'Místo',
    },
    {
        id: 'orderState',
        numeric: false,
        disablePadding: false,
        label: 'Zakázka',
        centered: true,
    },
    {
        id: 'paymentState',
        numeric: false,
        disablePadding: false,
        label: 'Platba',
        centered: true,
    },
    {
        id: 'profit',
        numeric: false,
        disablePadding: false,
        label: 'Zisk',
        align: 'right',
    },
    {
        id: 'productCount',
        numeric: false,
        disablePadding: false,
        label: 'Služby',
        centered: true,
    },
    {
        id: 'brigadierCount',
        numeric: false,
        disablePadding: false,
        label: 'Brigádníci',
        centered: true,
    },
    {
        id: 'carrierCount',
        numeric: false,
        disablePadding: false,
        label: 'Dopravci',
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface EventTableProps {
    events: EventBriefDto[]
    onDelete: (id: string) => void
}

const EventTable: React.FC<EventTableProps> = ({ events, onDelete }: EventTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('startsAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(200)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {events.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={events.length}
                        />
                        <TableBody>
                            {stableSort(
                                events.map((row) => {
                                    return {
                                        ...row,
                                        startsAt: new Date(row.startsAt).getTime(),
                                        endsAt: new Date(row.endsAt).getTime(),
                                        address: row.address
                                            ? (row.address?.street ?? '') +
                                            ' ' +
                                            (row.address?.city ?? '') +
                                            ' ' +
                                            (row.address?.zip ?? '')
                                            : '',
                                        orderStateChanges: '',
                                        paymentStateChanges: '',
                                        createdAt: new Date(row.createdAt).toLocaleDateString(),
                                        lastModifiedAt: new Date(row.lastModifiedAt).toLocaleDateString(),
                                        isEverythingReady: row.isEverythingReady ? 'Ano' : 'Ne',
                                        contactPhones: row.contactPhones ? row.contactPhones.join(', ') : '',
                                        hasContactWithEmail: row.hasContactWithEmail ? 'Ano' : 'Ne',
                                        emailMessagesLogs: '',
                                        contacts: '',
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={index}
                                            style={{
                                                backgroundColor: getEventTableRowColor(
                                                    row.paymentState,
                                                    theme,
                                                    row.orderState,
                                                ),
                                            }}
                                        >
                                            <CustomTableCell
                                                isText
                                                width={110}
                                                maxWidth={110}
                                                style={{ cursor: 'pointer', minWidth: 110 }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {new Date(row.startsAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                isText
                                                maxWidth={200}
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row.name}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                isText
                                                maxWidth={150}
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row.clientName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                isText
                                                maxWidth={300}
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row?.address ?? ''}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                center
                                                maxWidth={200}
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                <OrderStateTag orderState={row.orderState} />
                                            </CustomTableCell>
                                            <CustomTableCell
                                                center
                                                maxWidth={200}
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                <PaymentStateTag paymentState={row.paymentState} />
                                            </CustomTableCell>
                                            <CustomTableCell
                                                align='right'
                                                width={90}
                                                style={{ cursor: 'pointer', minWidth: 95 }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {getNumberRoundedInFormattedString(row.profit)}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                center
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row.productCount}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                center
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row.brigadierCount}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                center
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.id}
                                            >
                                                {row.carrierCount}
                                            </CustomTableCell>
                                            <CustomTableCell>
                                                <Stack spacing={2} direction='row'>
                                                    <TableButtonOpen route={RouteConstants.events} id={row.id} />
                                                    <TableButtonDelete
                                                        nameOfEntityInDialogMessage={row.name}
                                                        onClick={() => onDelete(row.id)}
                                                    />
                                                </Stack>
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[200, 300, 400]}
                    component='div'
                    count={events.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default EventTable
